import React from 'react';
import { useTranslation } from 'react-i18next';
import { Radio, styled, Typography, useTheme } from '@mui/material';

import { WorkerWillAssign } from '../../../types';

import RadioColorSmallCheckedSvgIcon from '../../../assets/SvgIcon/RadioColorSmallCheckedSvgIcon';
import RadioColorSmallUncheckedSvgIcon from '../../../assets/SvgIcon/RadioColorSmallUncheckedSvgIcon';

const Container = styled('div')`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Content = styled('div')`
  flex: 1;
  overflow-y: auto;
  margin-top: 10px;
  background-color: rgba(0, 0, 0, 0.2);
  padding: 0 10px 10px 10px;
  border-radius: 4px;
`;

const ContentTitle = styled(Typography)`
  color: white;
`;

const Section = styled('div')`
  padding: 12px 0;
  & + & {
    border-top: 1px solid ${({ theme }) => theme.color.secondary.$80};
  }
`;

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.color.box_bbg,
  fontFamily: 'Noto Sans',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 400,
}));

const SectionHint = styled(Typography)(({ theme }) => ({
  color: theme.color.gray.$235,
  fontFamily: 'Noto Sans',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 400,
}));

const OptionRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const OptionLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.color.box_bbg,
}));

type HabitValue = null | number;
type HabitField = 'diseases' | 'smoke' | 'alcohol';

interface WorkerHabitContentProps {
  handleWorkerDataWillAssign: (workerData: WorkerWillAssign) => void;
  data: WorkerWillAssign | undefined;
}

const WorkerHabitContent: React.FC<WorkerHabitContentProps> = ({
  handleWorkerDataWillAssign,
  data,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const handleChange = (field: HabitField, value: HabitValue) => {
    handleWorkerDataWillAssign({
      name: data?.name ?? '',
      imageURL: data?.imageURL ?? null,
      gender: data?.gender,
      birthday: data?.birthday ?? undefined,
      email: data?.email ?? undefined,
      phone: data?.phone ?? '',
      division: data?.division ?? undefined,
      trade: data?.trade ?? undefined,
      registrationNumber: data?.registrationNumber,
      groupIds: data?.groupIds ?? [],
      remark: data?.remark,
      orgId: data?.orgId ?? '',
      id: data?.id ?? '',
      avatarFile: data?.avatarFile,
      subContractorName: data?.subContractorName,
      height: data?.height ?? null,
      diseases: field === 'diseases' ? value : (data?.diseases ?? null),
      smoke: field === 'smoke' ? value : (data?.smoke ?? null),
      alcohol: field === 'alcohol' ? value : (data?.alcohol ?? null),
    });
  };

  const options: Array<{ value: HabitValue; labelKey: string }> = [
    { value: 0, labelKey: 'none' },
    { value: 1, labelKey: 'slight' },
    { value: 2, labelKey: 'severe' },
  ];

  const sections: Array<{ field: HabitField; titleKey: string }> = [
    {
      field: 'diseases',
      titleKey: 'diseases',
    },
    {
      field: 'smoke',
      titleKey: 'smoke',
    },
    {
      field: 'alcohol',
      titleKey: 'alcohol',
    },
  ];

  return (
    <Container>
      <ContentTitle>{t('project-settingJson.navigator.habit')}</ContentTitle>
      <Content>
        {sections.map(({ field, titleKey }) => {
          const currentValue = data?.[field] ?? null;
          return (
            <Section key={field}>
              <SectionTitle>{t(titleKey)}</SectionTitle>
              <SectionHint>
                {t(
                  'project-settingJson.page.worker-management.dialog.create-worker.habit.hint',
                )}
              </SectionHint>
              {options.map((opt) => (
                <OptionRow key={String(opt.value)}>
                  <Radio
                    checked={
                      opt.value === 0
                        ? currentValue === null || currentValue === 0
                        : currentValue === opt.value
                    }
                    onChange={() => handleChange(field, opt.value)}
                    icon={
                      <RadioColorSmallUncheckedSvgIcon
                        sx={{
                          color: '#fff',
                          width: '40px',
                          height: '40px',
                        }}
                      />
                    }
                    checkedIcon={
                      <RadioColorSmallCheckedSvgIcon
                        sx={{
                          color: theme.color.primary.$100,
                          width: '40px',
                          height: '40px',
                        }}
                      />
                    }
                    disableRipple
                    sx={{ width: '40px', height: '40px' }}
                  />
                  <OptionLabel>{t(opt.labelKey)}</OptionLabel>
                </OptionRow>
              ))}
            </Section>
          );
        })}
      </Content>
    </Container>
  );
};

export default WorkerHabitContent;
